import React from 'react';

import { WatchQueryFetchPolicy } from '@apollo/client';

import { Nullable } from '@clearverify/types';

import {
  PermissiblePurposeByReferenceItemFragment,
  PermissiblePurposeStatus,
  usePermissiblePurposeReferencesListWithPermissiblePurposesQuery,
  VerifierInfoFragment,
} from 'src/api';

import { getPermissiblePurposeReferenceOption } from './utils';

type PermissiblePurposesOption = {
  value: string;
  label: string;
};

interface PermissiblePurposesHookResult {
  permissiblePurposeReferenceOptions: PermissiblePurposesOption[];
  getRelatedPermissiblePurpose: (
    permissiblePurposeReferenceId: Nullable<string>,
  ) => Nullable<PermissiblePurposeByReferenceItemFragment>;
  isPermissiblePurposeReferencesLoading: boolean;
  havePermissiblePurposePending: boolean;
}

export const useManualVerifyPermissiblePurposeReferenceOptions = (
  verifier: Nullable<VerifierInfoFragment>,
  fetchPolicy: WatchQueryFetchPolicy = 'cache-first',
): PermissiblePurposesHookResult => {
  const { data: permissiblePurposeReferenceData, loading: isPermissiblePurposeReferencesLoading } =
    usePermissiblePurposeReferencesListWithPermissiblePurposesQuery({
      variables: {
        filter: {
          verifierType: {
            equals: verifier?.type,
          },
        },
        permissiblePurposeFilter: {
          verifierId: { equals: verifier?.id },
        },
      },
      fetchPolicy,
    });

  const {
    permissiblePurposeReferenceOptions,
    havePermissiblePurposePending,
    getRelatedPermissiblePurpose,
  } = React.useMemo(() => {
    const permissiblePurposeReferenceList =
      permissiblePurposeReferenceData?.permissiblePurposeReferenceList ?? [];

    const getRelatedPermissiblePurpose: PermissiblePurposesHookResult['getRelatedPermissiblePurpose'] =
      purposeReferenceValue => {
        const purposeReference = permissiblePurposeReferenceList.find(
          purposeReference => purposeReference.id === purposeReferenceValue,
        );
        if (purposeReference?.permissiblePurposesCount === 0) return null;
        // according to https://oncentive.atlassian.net/browse/CLEAR-1316 verifier should not be able to request same permissible purpose twice
        // that's why we are returning only first item
        return purposeReference?.permissiblePurposesList[0];
      };

    const permissiblePurposeReferenceOptions = [...permissiblePurposeReferenceList]
      .sort((a, b) => {
        const isAPending = a.permissiblePurposesList.some(
          item => item.status === PermissiblePurposeStatus.Pending,
        );

        const isBPending = b.permissiblePurposesList.some(
          item => item.status === PermissiblePurposeStatus.Pending,
        );

        if (isAPending && isBPending) return 0;
        if (isAPending) return -1;
        return 1;
      })
      .map(item => getPermissiblePurposeReferenceOption(item));

    const havePermissiblePurposePending = permissiblePurposeReferenceList.some(referenceItem =>
      referenceItem.permissiblePurposesList.some(
        item => item.status === PermissiblePurposeStatus.Pending,
      ),
    );

    return {
      permissiblePurposeReferenceOptions,
      havePermissiblePurposePending,
      getRelatedPermissiblePurpose,
    };
  }, [permissiblePurposeReferenceData?.permissiblePurposeReferenceList]);

  return {
    permissiblePurposeReferenceOptions,
    havePermissiblePurposePending,
    isPermissiblePurposeReferencesLoading,
    getRelatedPermissiblePurpose,
  };
};
